import React, { useState } from 'react';
import { X, Building2, User, Mail, Tag, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { createCompany } from '../../../../services/companyService';

const AddCompanyModal = ({ onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    companyTag: '',
    ownerName: '',
    ownerEmail: '',
    ownerPassword: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const result = await createCompany({
        companyName: formData?.companyName,
        companyTag: formData?.companyTag,
        ownerName: formData?.ownerName,
        ownerEmail: formData?.ownerEmail,
        ownerPassword: formData?.ownerPassword
      });

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to create company');
      }

      setIsSubmitting(false);
      setSuccessData(result?.data);
    } catch (err) {
      setError(err?.message || 'Failed to create company. Please try again.');
      setIsSubmitting(false);
    }
  };

  /* Success View */
  if (successData) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-[#1a1a1a] border border-border rounded-lg max-w-md w-full p-6 text-center">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">Company Created!</h2>
          <p className="text-text-secondary text-sm mb-6">The company has been set up and the owner account created with the password you provided.</p>

          <div className="bg-[#0f0f0f] border border-border rounded-lg p-4 mb-6 text-left">
            <div className="mb-3">
              <p className="text-xs text-text-secondary uppercase font-semibold mb-1">Company Name</p>
              <p className="text-text-primary font-medium text-sm">{successData?.company?.companyName}</p>
            </div>
            <div className="mb-3">
              <p className="text-xs text-text-secondary uppercase font-semibold mb-1">Owner Email</p>
              <p className="text-text-primary font-mono text-sm">{successData?.primaryOwner?.email}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary uppercase font-semibold mb-1">Login Instructions</p>
              <p className="text-text-secondary text-xs">The owner can now log in at /corporate-login using their email and the password you set.</p>
            </div>
          </div>

          <button
            onClick={() => onSuccess(successData?.message)}
            className="w-full px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a1a] border border-border rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-[#1a1a1a] z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Add New Company</h2>
              <p className="text-text-secondary text-sm">Create a new company with primary owner</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 form-enabled">
          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <p className="text-red-400 text-sm font-medium">Error creating company</p>
                  <p className="text-red-300 text-sm mt-1">{error}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setError('')}
                  className="text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Company Information */}
          <div>
            <h3 className="text-text-primary font-medium mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-orange-400" />
              Company Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-text-primary text-sm font-medium mb-2">
                  Company Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData?.companyName}
                  onChange={handleInputChange}
                  placeholder="Acme Corporation"
                  required
                  className="w-full px-4 py-2.5 bg-[#0f0f0f] border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-text-primary text-sm font-medium mb-2">
                  Company Tag <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                  <input
                    type="text"
                    name="companyTag"
                    value={formData?.companyTag}
                    onChange={handleInputChange}
                    placeholder="acme01"
                    required
                    pattern="[a-z0-9]{2,7}"
                    maxLength={7}
                    className="w-full pl-10 pr-4 py-2.5 bg-[#0f0f0f] border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                  />
                </div>
                <p className="text-text-secondary text-xs mt-1">2-7 lowercase letters and numbers only (unique per company)</p>
              </div>
            </div>
          </div>

          {/* Primary Owner Information */}
          <div>
            <h3 className="text-text-primary font-medium mb-4 flex items-center gap-2">
              <User className="w-4 h-4 text-orange-400" />
              Primary Owner
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-text-primary text-sm font-medium mb-2">
                  Owner Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="ownerName"
                  value={formData?.ownerName}
                  onChange={handleInputChange}
                  placeholder="John Smith"
                  required
                  className="w-full px-4 py-2.5 bg-[#0f0f0f] border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-text-primary text-sm font-medium mb-2">
                  Owner Email <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                  <input
                    type="email"
                    name="ownerEmail"
                    value={formData?.ownerEmail}
                    onChange={handleInputChange}
                    placeholder="john@acme.com"
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-[#0f0f0f] border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-text-primary text-sm font-medium mb-2">
                  Owner Password <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="ownerPassword"
                    value={formData?.ownerPassword}
                    onChange={handleInputChange}
                    placeholder="Set password for owner"
                    required
                    minLength={8}
                    className="w-full pl-10 pr-12 py-2.5 bg-[#0f0f0f] border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-text-secondary text-xs mt-1">Minimum 8 characters. Owner can change this later.</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-surface hover:bg-surface-light text-text-primary rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating...' : 'Create Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCompanyModal;
